import React, { useState } from 'react';
import { Card } from '@/components/ui/Card';
import { LeadsChartData, TimePeriod } from '@/types/dashboard.types';

interface LeadsChartProps {
  data: LeadsChartData[];
  isLoading?: boolean;
  onPeriodChange?: (period: TimePeriod) => void;
}

const periodOptions: { value: TimePeriod; label: string }[] = [
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'quarter', label: 'This Quarter' },
];

const LoadingSkeleton: React.FC = () => (
  <div className="animate-pulse">
    <div className="flex items-center justify-between mb-6">
      <div className="h-6 bg-secondary-200 rounded w-32"></div>
      <div className="h-8 bg-secondary-100 rounded w-28"></div>
    </div>
    <div className="h-64 bg-secondary-100 rounded-lg"></div>
  </div>
);

export const LeadsChart: React.FC<LeadsChartProps> = ({
  data,
  isLoading = false,
  onPeriodChange,
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>('month');

  const handlePeriodChange = (period: TimePeriod) => {
    setSelectedPeriod(period);
    onPeriodChange?.(period);
  };

  if (isLoading) {
    return (
      <Card className="h-full">
        <LoadingSkeleton />
      </Card>
    );
  }

  // Calculate max value for scaling
  const maxValue = Math.max(...data.map((d) => Math.max(d.leads, d.qleads)), 1);
  const chartHeight = 200;

  // Calculate totals
  const totalLeads = data.reduce((sum, d) => sum + d.leads, 0);
  const totalQLeads = data.reduce((sum, d) => sum + d.qleads, 0);

  return (
    <Card className="h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-secondary-900">Leads Overview</h3>
          <p className="text-sm text-secondary-500 mt-1">
            {totalLeads} total leads, {totalQLeads} Q-Leads
          </p>
        </div>
        <select
          value={selectedPeriod}
          onChange={(e) => handlePeriodChange(e.target.value as TimePeriod)}
          className="text-sm border border-secondary-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          {periodOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-6 mb-4">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-primary-500"></div>
          <span className="text-sm text-secondary-600">All Leads</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-amber-500"></div>
          <span className="text-sm text-secondary-600">Q-Leads</span>
        </div>
      </div>

      {/* Chart */}
      {data.length === 0 ? (
        <div className="flex items-center justify-center h-52 bg-secondary-50 rounded-lg">
          <p className="text-secondary-500">No data available</p>
        </div>
      ) : (
        <div className="relative">
          {/* Y-axis labels */}
          <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-xs text-secondary-400 pr-2">
            <span>{maxValue}</span>
            <span>{Math.round(maxValue / 2)}</span>
            <span>0</span>
          </div>

          {/* Chart area */}
          <div className="ml-8">
            {/* Grid lines */}
            <div className="absolute inset-x-8 top-0" style={{ height: chartHeight }}>
              <div className="absolute w-full border-t border-secondary-100" style={{ top: '0%' }}></div>
              <div className="absolute w-full border-t border-secondary-100" style={{ top: '50%' }}></div>
              <div className="absolute w-full border-t border-secondary-200" style={{ top: '100%' }}></div>
            </div>

            {/* Bars */}
            <div
              className="flex items-end justify-around gap-2"
              style={{ height: chartHeight }}
            >
              {data.map((item, index) => {
                const leadsHeight = (item.leads / maxValue) * 100;
                const qleadsHeight = (item.qleads / maxValue) * 100;

                return (
                  <div
                    key={index}
                    className="flex-1 flex flex-col items-center group"
                  >
                    <div className="relative w-full flex justify-center gap-1 h-full items-end">
                      {/* All Leads bar */}
                      <div
                        className="w-5 bg-primary-500 rounded-t-sm transition-all duration-300 hover:bg-primary-600 relative"
                        style={{ height: `${leadsHeight}%`, minHeight: item.leads > 0 ? '4px' : '0' }}
                      >
                        {/* Tooltip */}
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                          <div className="bg-secondary-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                            {item.leads} leads
                          </div>
                        </div>
                      </div>
                      {/* Q-Leads bar */}
                      <div
                        className="w-5 bg-amber-500 rounded-t-sm transition-all duration-300 hover:bg-amber-600 relative"
                        style={{ height: `${qleadsHeight}%`, minHeight: item.qleads > 0 ? '4px' : '0' }}
                      >
                        {/* Tooltip */}
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                          <div className="bg-secondary-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                            {item.qleads} Q-Leads
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* X-axis labels */}
            <div className="flex justify-around mt-3">
              {data.map((item, index) => (
                <span
                  key={index}
                  className="text-xs text-secondary-500 text-center flex-1 truncate px-1"
                >
                  {item.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default LeadsChart;
